// Client Internal Audit — audit plan builder. SINGLE SOURCE for the plan layout used by the job page (edit +
// preview), the client's view-only plan page and the plan email.
//
// CCIS STANDARD AUDIT PLAN (approved by the CCIS auditor, 5 Oct 2026 — replaces the old Word plans). One element list
// in the common ISO order (clause 4 → 10); each row carries the clause(s) PER STANDARD, and a plan shows the rows where
// at least one of its standards has a clause — so every combination of standards gives the same, consistent layout.
//   • Morning: top management. Afternoon (after lunch): functional heads.
//   • ISO/IEC 27001 on its own: clauses 4–10 in the morning, the Annex A controls after lunch.
//   • ISO/IEC 17025 (laboratories): its own list, clause by clause 4 → 8 (management system last).
//   • Site walk: optional per audit (tick-box on the job page) — only for clients where the auditor walks the site.
// Every clause number and its title were checked against the CCIS checklists. The admin only picks the duration +
// start time; items stay editable per job.

const K = { 'ISO 9001:2015': '9K', 'ISO 14001:2015': '14K', 'ISO 45001:2018': '45K', 'ISO/IEC 27001:2022': '27K',
  'ISO/IEC 42001:2023': '42K', 'ISO 22301:2019': '22K', 'SS 651:2019': '651', 'ISO/IEC 17025:2017': '17K' }
const TOP = 'TOP MGT / MGT REPS'
const FH = 'FUNCTIONAL HEADS'
const QM = 'TOP MGT / QUALITY MANAGER'
const LAB = 'LAB MANAGER / TECHNICAL STAFF'

// [element, { standard key: clause }, who, part: 'am' | 'pm', { e9: wording when ISO 9001 is in the audit, annexA }]
const MASTER = [
  // ── Morning: top management ──
  ['Context of the organization • Needs and expectations of interested parties', { '9K': '4.1, 4.2', '14K': '4.1, 4.2', '45K': '4.1, 4.2', '27K': '4.1, 4.2', '22K': '4.1, 4.2.1', '651': '4.1, 4.2' }, TOP, 'am'],
  ['Scope of the management system • Management system and its processes', { '9K': '4.3, 4.4', '14K': '4.3, 4.4', '45K': '4.3, 4.4', '27K': '4.3, 4.4', '22K': '4.3, 4.4', '651': '4.3, 4.4' }, TOP, 'am'],
  ['Leadership and commitment', { '9K': '5.1.1', '14K': '5.1', '45K': '5.1', '27K': '5.1', '22K': '5.1', '651': '5.1' }, TOP, 'am'],
  ['Customer focus', { '9K': '5.1.2' }, TOP, 'am'],
  ['Policy', { '9K': '5.2', '14K': '5.2', '45K': '5.2', '27K': '5.2', '22K': '5.2', '651': '5.2' }, TOP, 'am'],
  ['Organizational roles, responsibilities and authorities', { '9K': '5.3', '14K': '5.3', '45K': '5.3', '27K': '5.3', '22K': '5.3', '651': '5.3' }, TOP, 'am'],
  ['Consultation and participation of workers', { '45K': '5.4', '651': '5.4' }, TOP, 'am'],
  ['Actions to address risks and opportunities', { '9K': '6.1', '14K': '6.1.1, 6.1.4', '45K': '6.1.1, 6.1.4', '27K': '6.1.1', '22K': '6.1', '651': '6.1.1, 6.1.4' }, TOP, 'am'],
  ['Environmental aspects', { '14K': '6.1.2' }, TOP, 'am'],
  ['Hazard identification and assessment of risks and opportunities', { '45K': '6.1.2', '651': '6.1.2' }, TOP, 'am'],
  ['Compliance obligations • Legal and other requirements', { '14K': '6.1.3', '45K': '6.1.3', '22K': '4.2.2', '651': '6.1.3' }, TOP, 'am'],
  ['Information security risk assessment and risk treatment (planning)', { '27K': '6.1.2, 6.1.3' }, TOP, 'am'],
  ['Objectives and planning to achieve them', { '9K': '6.2', '14K': '6.2', '45K': '6.2', '27K': '6.2', '22K': '6.2', '651': '6.2' }, TOP, 'am'],
  ['Planning of changes', { '9K': '6.3', '27K': '6.3', '22K': '6.3' }, TOP, 'am'],
  ['Management review', { '9K': '9.3', '14K': '9.3', '45K': '9.3', '27K': '9.3', '22K': '9.3', '651': '9.3' }, TOP, 'am'],
  ['Continual improvement', { '9K': '10.1, 10.3', '14K': '10.1, 10.3', '45K': '10.1, 10.3', '27K': '10.1', '22K': '10.2', '651': '10.1, 10.3' }, TOP, 'am'],
  // ── Afternoon: functional heads ──
  ['Resources', { '9K': '7.1.1, 7.1.2, 7.1.3, 7.1.4, 7.1.6', '14K': '7.1', '45K': '7.1', '27K': '7.1', '22K': '7.1', '651': '7.1' }, FH, 'pm',
    { e9: 'Resources • People • Infrastructure • Environment for the operation of processes • Organizational knowledge' }],
  ['Monitoring and measuring resources (calibration)', { '9K': '7.1.5' }, FH, 'pm'],
  ['Competence • Awareness (training)', { '9K': '7.2, 7.3', '14K': '7.2, 7.3', '45K': '7.2, 7.3', '27K': '7.2, 7.3', '22K': '7.2, 7.3', '651': '7.2, 7.3' }, FH, 'pm'],
  ['Communication', { '9K': '7.4', '14K': '7.4', '45K': '7.4', '27K': '7.4', '22K': '7.4', '651': '7.4' }, FH, 'pm'],
  ['Documented information (document and record control)', { '9K': '7.5', '14K': '7.5', '45K': '7.5', '27K': '7.5', '22K': '7.5', '651': '7.5' }, FH, 'pm'],
  ['Operational planning and control', { '9K': '8.1', '14K': '8.1', '45K': '8.1.1', '27K': '8.1', '22K': '8.1', '651': '8.1.1' }, FH, 'pm'],
  ['Eliminating hazards and reducing OH&S / S&H risks', { '45K': '8.1.2', '651': '8.1.2' }, FH, 'pm'],
  ['In-house S&H rules and regulations', { '651': '8.1.3' }, FH, 'pm'],
  ['Operating procedures', { '651': '8.1.4' }, FH, 'pm'],
  ['Safe work practices', { '651': '8.1.5' }, FH, 'pm'],
  ['Pre-start-up safety', { '651': '8.1.6' }, FH, 'pm'],
  ['Mechanical integrity and reliability', { '651': '8.1.7' }, FH, 'pm'],
  ['Control of hazardous substances', { '651': '8.1.8' }, FH, 'pm'],
  ['Occupational health', { '651': '8.1.9' }, FH, 'pm'],
  ['Management of change', { '45K': '8.1.3', '651': '8.1.10' }, FH, 'pm'],
  ['Requirements for products and services (sales / contract review)', { '9K': '8.2' }, FH, 'pm'],
  ['Design and development of products and services, if applicable', { '9K': '8.3' }, FH, 'pm'],
  ['Purchasing • Externally provided processes, products and services • Contractors', { '9K': '8.4', '45K': '8.1.4', '651': '8.1.11' }, FH, 'pm'],
  ['Production and service provision (incl. identification and traceability, customer property, preservation, post-delivery, control of changes)', { '9K': '8.5' }, FH, 'pm'],
  ['Release of products and services • Control of nonconforming outputs', { '9K': '8.6, 8.7' }, FH, 'pm'],
  ['Information security risk assessment and risk treatment (operation)', { '27K': '8.2, 8.3' }, FH, 'pm'],
  ['Business impact analysis and risk assessment', { '22K': '8.2' }, FH, 'pm'],
  ['Business continuity strategies and solutions', { '22K': '8.3' }, FH, 'pm'],
  ['Business continuity plans and procedures', { '22K': '8.4' }, FH, 'pm'],
  ['Exercise programme', { '22K': '8.5' }, FH, 'pm'],
  ['Evaluation of business continuity documentation and capabilities', { '22K': '8.6' }, FH, 'pm'],
  ['Emergency preparedness and response', { '14K': '8.2', '45K': '8.2', '651': '8.2' }, FH, 'pm'],
  ['Monitoring, measurement, analysis and evaluation', { '9K': '9.1.1, 9.1.3', '14K': '9.1.1', '45K': '9.1.1', '27K': '9.1', '22K': '9.1', '651': '9.1.1, 9.1.2, 9.1.4' }, FH, 'pm'],
  ['Customer satisfaction', { '9K': '9.1.2' }, FH, 'pm'],
  ['Evaluation of compliance', { '14K': '9.1.2', '45K': '9.1.2', '651': '9.1.3' }, FH, 'pm'],
  ['Internal audit', { '9K': '9.2', '14K': '9.2', '45K': '9.2', '27K': '9.2', '22K': '9.2', '651': '9.2' }, FH, 'pm'],
  ['Incident, nonconformity and corrective action', { '9K': '10.2', '14K': '10.2', '45K': '10.2', '27K': '10.2', '22K': '10.1', '651': '10.2' }, FH, 'pm'],
  // ISO/IEC 27001 Statement of Applicability (Annex A)
  ['Statement of Applicability – A.5 Organizational controls', { '27K': 'A.5' }, FH, 'pm', { annexA: true }],
  ['Statement of Applicability – A.6 People controls', { '27K': 'A.6' }, FH, 'pm', { annexA: true }],
  ['Statement of Applicability – A.7 Physical controls', { '27K': 'A.7' }, FH, 'pm', { annexA: true }],
  ['Statement of Applicability – A.8 Technological controls', { '27K': 'A.8' }, FH, 'pm', { annexA: true }],
]

// ISO/IEC 17025:2017 (laboratories) — clause by clause, management system (clause 8) last; lunch before clause 7.
const MASTER_17025 = [
  ['Impartiality • Confidentiality', { '17K': '4.1, 4.2' }, QM, 'am'],
  ['Structural requirements (legal entity, management, scope of laboratory activities)', { '17K': '5' }, QM, 'am'],
  ['Personnel (competence, training, supervision, authorization)', { '17K': '6.1, 6.2' }, LAB, 'am'],
  ['Facilities and environmental conditions', { '17K': '6.3' }, LAB, 'am'],
  ['Equipment (maintenance, calibration)', { '17K': '6.4' }, LAB, 'am'],
  ['Metrological traceability', { '17K': '6.5' }, LAB, 'am'],
  ['Externally provided products and services (purchasing, vendor control)', { '17K': '6.6' }, 'PURCHASING / LAB MANAGER', 'am'],
  ['Review of requests, tenders and contracts (sales contract review)', { '17K': '7.1' }, 'SALES', 'pm'],
  ['Selection, verification and validation of methods', { '17K': '7.2' }, LAB, 'pm'],
  ['Sampling • Handling of test or calibration items', { '17K': '7.3, 7.4' }, LAB, 'pm'],
  ['Technical records', { '17K': '7.5' }, LAB, 'pm'],
  ['Evaluation of measurement uncertainty • Ensuring the validity of results', { '17K': '7.6, 7.7' }, LAB, 'pm'],
  ['Reporting of results', { '17K': '7.8' }, 'LAB MANAGER / ADMINISTRATION', 'pm'],
  ['Complaints • Nonconforming work', { '17K': '7.9, 7.10' }, 'QUALITY MANAGER / LAB MANAGER', 'pm'],
  ['Control of data and information management', { '17K': '7.11' }, 'LAB MANAGER / IT', 'pm'],
  ['Management system options • Management system documentation', { '17K': '8.1, 8.2' }, QM, 'pm'],
  ['Control of management system documents • Control of records', { '17K': '8.3, 8.4' }, 'QUALITY MANAGER', 'pm'],
  ['Actions to address risks and opportunities', { '17K': '8.5' }, QM, 'pm'],
  ['Improvement • Corrective actions', { '17K': '8.6, 8.7' }, 'QUALITY MANAGER', 'pm'],
  ['Internal audits • Management reviews', { '17K': '8.8, 8.9' }, QM, 'pm'],
]

// ISO/IEC 27001:2022 & ISO/IEC 42001:2023 — the CCIS combined plan, row for row as "AUDIT PLAN-LINKCIRCLE (27001 &
// 42001).docx" (owner, 4 Nov 2026): element wording, each standard's clause number (blank where the plan has none,
// e.g. 27001 has no 6.1.4 / 6.3 / 8.4 here) and who attends. Used ONLY when the audit is exactly these two standards.
// [element, 27001 clause, 42001 clause, who, { bold, heading, lunch }]
const TMF = 'TOP MGT / MGT REPS / FUNCTIONAL HEADS'
const MASTER_27K42K = [
  ['TOP MANAGEMENT', '', '', TMF, { heading: true }],
  ['Context of the organization', '4', '4', TMF, { bold: true }],
  ['Understanding the organization and its context', '4.1', '4.1', TMF],
  ['Understanding the needs and expectations of interested parties', '4.2', '4.2', TMF],
  ['Determining the scope of the information security / AI management system', '4.3', '4.3', TMF],
  ['Information security / AI management system', '4.4', '4.4', TMF],
  ['Leadership', '5', '5', TMF, { bold: true }],
  ['Leadership and commitment', '5.1', '5.1', TMF],
  ['Policy / AI policy', '5.2', '5.2', TMF],
  ['Organizational roles, responsibilities and authorities', '5.3', '5.3', TMF],
  ['Planning', '6', '6', TMF, { bold: true }],
  ['Actions to address risks and opportunities', '6.1', '6.1', TMF],
  ['General', '6.1.1', '6.1.1', TMF],
  ['Information security / AI risk assessment', '6.1.2', '6.1.2', TMF],
  ['Information security / AI risk treatment', '6.1.3', '6.1.3', TMF],
  ['AI system impact assessment', '', '6.1.4', TMF],
  ['Information security / AI objectives and planning to achieve them', '6.2', '6.2', TMF],
  ['Planning of changes', '', '6.3', TMF],
  ['Support', '7', '7', FH, { bold: true }],
  ['Resources', '7.1', '7.1', FH],
  ['Competence', '7.2', '7.2', FH],
  ['Awareness', '7.3', '7.3', FH],
  ['Communication', '7.4', '7.4', FH],
  ['Documented information', '7.5', '7.5', FH],
  ['General', '7.5.1', '7.5.1', FH],
  ['Creating and updating', '7.5.2', '7.5.2', FH],
  ['Control of documented information', '7.5.3', '7.5.3', FH],
  ['Operation', '8', '8', FH, { bold: true }],
  ['Operational planning and control', '8.1', '8.1', FH],
  ['Information security / AI risk assessment', '8.2', '8.2', FH],
  ['Information security / AI risk treatment', '8.3', '8.3', FH],
  ['AI system impact assessment', '', '8.4', FH],
  ['Performance evaluation', '9', '9', FH, { bold: true }],
  ['Monitoring, measurement, analysis and evaluation', '9.1', '9.1', FH],
  ['Internal audit', '9.2', '9.2', FH],
  ['General', '9.2.1', '9.2.1', FH],
  ['Internal audit programme', '9.2.2', '9.2.2', FH],
  ['Management review', '9.3', '9.3', FH],
  ['General', '9.3.1', '9.3.1', FH],
  ['Management review inputs', '9.3.2', '9.3.2', FH],
  ['Management review results', '9.3.3', '9.3.3', FH],
  ['Improvement', '10', '10', FH, { bold: true }],
  ['Continual improvement', '10.1', '10.1', FH],
  ['Nonconformity and corrective action', '10.2', '10.2', FH],
  // Page 3 of the plan: the Statements of Applicability (no clause numbers or attendees in the plan)
  ['Statement of Applicability for ISO/IEC 27001:2022', '', '', '', { bold: true, lunch: true }],
  ['A5 – Organizational controls', '', '', ''],
  ['A6 – People controls', '', '', ''],
  ['A7 – Physical controls', '', '', ''],
  ['A8 – Technological controls', '', '', ''],
  ['Statement of Applicability for ISO/IEC 42001:2023', '', '', '', { bold: true }],
  ['A.2 – Policies related to AI', '', '', ''],
  ['A.3 – Internal organization', '', '', ''],
  ['A.4 – Resources for AI systems', '', '', ''],
  ['A.5 – Assessing impacts of AI systems', '', '', ''],
  ['A.6 – AI system life cycle', '', '', ''],
  ['A.7 – Data for AI systems', '', '', ''],
  ['A.8 – Information for interested parties of AI systems', '', '', ''],
  ['A.9 – Use of AI systems', '', '', ''],
  ['A.10 – Third-party and customer relationships', '', '', ''],
]
// ISO 37001:2025 (anti-bribery) — the CCIS plan, row for row as "AUDIT PLAN-BUCG.doc" (owner, 5 Nov 2026): element
// wording, clause numbers and who attends (clauses 4–5 top management, 6–10 functional heads). Used only for an
// ISO 37001 audit on its own. [element, clause, who, { bold, heading }]
const MASTER_37K = [
  ['TOP MANAGEMENT', '', TMF, { heading: true }],
  ['Context of the organization', '4', TMF, { bold: true }],
  ['Understanding the organization and its context', '4.1', TMF],
  ['Understanding the needs and expectations of interested parties', '4.2', TMF],
  ['Determining the scope of the anti-bribery management system', '4.3', TMF],
  ['Anti-bribery management system', '4.4', TMF],
  ['Bribery risk assessment', '4.5', TMF],
  ['Leadership', '5', TMF, { bold: true }],
  ['Leadership and commitment', '5.1', TMF],
  ['Governing body', '5.1.1', TMF],
  ['Top management', '5.1.2', TMF],
  ['Anti-bribery culture', '5.1.3', TMF],
  ['Anti-bribery policy', '5.2', TMF],
  ['Roles, responsibilities and authorities', '5.3', TMF],
  ['General', '5.3.1', TMF],
  ['Anti-bribery function', '5.3.2', TMF],
  ['Delegated decision-making', '5.3.3', TMF],
  ['Planning', '6', FH, { bold: true }],
  ['Actions to address risks and opportunities', '6.1', FH],
  ['Anti-bribery objectives and planning to achieve them', '6.2', FH],
  ['Planning of changes', '6.3', FH],
  ['Support', '7', FH, { bold: true }],
  ['Resources', '7.1', FH],
  ['Competence', '7.2', FH],
  ['General', '7.2.1', FH],
  ['Employment process', '7.2.2', FH],
  ['Awareness', '7.3', FH],
  ['Awareness of personnel', '7.3.1', FH],
  ['Training for personnel', '7.3.2', FH],
  ['Training for business associates', '7.3.3', FH],
  ['Awareness and training programmes', '7.3.4', FH],
  ['Communication', '7.4', FH],
  ['Documented information', '7.5', FH],
  ['General', '7.5.1', FH],
  ['Creating and updating documented information', '7.5.2', FH],
  ['Control of documented information', '7.5.3', FH],
  ['Operation', '8', FH, { bold: true }],
  ['Operational planning and control', '8.1', FH],
  ['Due diligence', '8.2', FH],
  ['Financial controls', '8.3', FH],
  ['Non-financial controls', '8.4', FH],
  ['Implementation of anti-bribery controls by controlled organizations and by business associates', '8.5', FH],
  ['Anti-bribery commitments', '8.6', FH],
  ['Gifts, hospitality, donations and similar benefits', '8.7', FH],
  ['Managing inadequacy of anti-bribery controls', '8.8', FH],
  ['Raising concerns', '8.9', FH],
  ['Investigating and dealing with bribery', '8.10', FH],
  ['Performance evaluation', '9', FH, { bold: true }],
  ['Monitoring, measurement, analysis and evaluation', '9.1', FH],
  ['Internal audit', '9.2', FH],
  ['General', '9.2.1', FH],
  ['Internal audit programme', '9.2.2', FH],
  ['Audit procedures, controls and systems', '9.2.3', FH],
  ['Objectivity and impartiality', '9.2.4', FH],
  ['Management review', '9.3', FH],
  ['General', '9.3.1', FH],
  ['Management review inputs', '9.3.2', FH],
  ['Management review results', '9.3.3', FH],
  ['Review by anti-bribery function', '9.4', FH],
  ['Improvement', '10', FH, { bold: true }],
  ['Continual improvement', '10.1', FH],
  ['Nonconformity and corrective action', '10.2', FH],
]
// ISO 50001:2018 & ISO 46001:2019 (energy & water efficiency) — the CCIS combined plan, row for row as
// "ISO 50K+46K AUDIT PLAN.docx" (owner, 5 Nov 2026): element wording, each standard's clause number (blank where the
// plan has none) and who attends (4–5 top management, 6–10 functional heads). Two typos in the plan corrected:
// "Communicatioin" → "Communication", "energy date" → "energy data". [element, 50001, 46001, who, { bold }]
const MASTER_50K46K = [
  ['Context of the organization', '4', '4', TMF, { bold: true }],
  ['Understanding the organization and its context', '4.1', '4.1', TMF],
  ['Understanding the needs and expectations of interested parties', '4.2', '4.2', TMF],
  ['Determining the scope of the energy/water efficiency management system', '4.3', '4.3', TMF],
  ['Energy/Water efficiency management system', '4.4', '4.4', TMF],
  ['Leadership', '5', '5', TMF, { bold: true }],
  ['Leadership and commitment', '5.1', '5.1', TMF],
  ['Energy policy / Policy', '5.2', '5.2', TMF],
  ['Organization roles, responsibilities and authorities', '5.3', '5.3', TMF],
  ['Planning', '6', '6', FH, { bold: true }],
  ['Actions to address risks and opportunities', '6.1', '6.1', FH],
  ['Objectives, energy targets and planning to address them', '6.2', '', FH],
  ['Water efficiency objectives and planning to achieve them', '', '6.2', FH],
  ['General', '', '6.2.1', FH],
  ['Planning', '', '6.2.2', FH],
  ['Legal requirements or other requirements', '', '6.2.3', FH],
  ['Conduct water use review', '', '6.2.4', FH],
  ['Identify business activity indicator(s)', '', '6.2.5', FH],
  ['Determine water efficiency indicator(s)', '', '6.2.6', FH],
  ['Identify baseline water efficiency indicator(s)', '', '6.2.7', FH],
  ['Energy review', '6.3', '', FH],
  ['Targets and action plans', '', '6.3', FH],
  ['Energy performance indicators', '6.4', '', FH],
  ['Energy baseline', '6.5', '', FH],
  ['Planning for collection of energy data', '6.6', '', FH],
  ['Support', '7', '7', FH, { bold: true }],
  ['Resources', '7.1', '7.1', FH],
  ['Competence', '7.2', '7.2', FH],
  ['Awareness', '7.3', '7.3', FH],
  ['Communication', '7.4', '7.4', FH],
  ['Documented information', '7.5', '7.5', FH],
  ['General', '7.5.1', '7.5.1', FH],
  ['Creating and updating', '7.5.2', '7.5.2', FH],
  ['Control of documented information', '7.5.3', '7.5.3', FH],
  ['Operation', '8', '8', FH, { bold: true }],
  ['Operational planning and control', '8.1', '8.1', FH],
  ['Design', '8.2', '8.2', FH],
  ['Procurement', '8.3', '', FH],
  ['Procurement of water services, products and equipment', '', '8.3', FH],
  ['Maintenance and inspection', '', '8.4', FH],
  ['Performance evaluation', '9', '9', FH, { bold: true }],
  ['Monitoring, measurement, analysis and evaluation', '9.1', '9.1', FH],
  ['Internal audit', '9.2', '9.2', FH],
  ['General', '9.2.1', '9.2.1', FH],
  ['Internal audit programme', '9.2.2', '9.2.2', FH],
  ['Management review', '9.3', '9.3', FH],
  ['Improvement', '10', '10', FH, { bold: true }],
  ['Nonconformity and corrective action', '10.1', '10.1', FH],
  ['Continual improvement', '10.2', '10.2', FH],
]
const PAIR_50K46K = ['ISO 50001:2018', 'ISO 46001:2019']
const isPair50K46K = standards => sameSet(standards, PAIR_50K46K)

const ONLY_37K = ['ISO 37001:2025']
const isOnly37K = standards => sameSet(standards, ONLY_37K)

const PAIR_27K42K = ['ISO/IEC 27001:2022', 'ISO/IEC 42001:2023']
const isPair27K42K = standards => sameSet(standards, PAIR_27K42K)

// Version of the standard list above. Raise it whenever MASTER changes: plans made from an older version (e.g. a
// recurring client's plan carried forward from last year) then offer "A newer CCIS audit plan is available".
export const PLAN_VERSION = 1

// Site inspection — optional per audit (job page tick-box), always the LAST row of the plan (CCIS auditor, 5 Oct
// 2026). Never pre-ticked (owner, 4 Nov 2026) — only a person ticks it.
export const SITE_INSPECTION = { element: 'Site inspection', who: FH, siteWalk: true }

const keysOf = standards => (standards || []).map(s => K[s] || null)
const sameSet = (a, b) => (a || []).length === (b || []).length && (a || []).every(x => (b || []).includes(x))
export const sameStandards = sameSet

// Which standards have no plan rows (e.g. a standard typed under "Other") — the office adds those items by hand.
// ISO/IEC 42001 has plan rows only in the combined 27001 & 42001 plan (no other CCIS plan includes it yet).
export function unplannedStandards(standards) {
  if (isPair27K42K(standards) || isOnly37K(standards) || isPair50K46K(standards)) return []
  return (standards || []).filter(s => !K[s] || K[s] === '42K')
}

// Plan items for a set of standards: [{ element, clauses: [per standard], who, heading: false, lunch? }]
export function defaultPlanItems(standards) {
  // ISO/IEC 27001 & ISO/IEC 42001 together: the CCIS combined plan, as written (columns follow the standards' order).
  if (isPair27K42K(standards)) {
    return MASTER_27K42K.map(([element, c27, c42, who, o = {}]) => ({
      element, clauses: (standards || []).map(s => (s === PAIR_27K42K[0] ? c27 : c42)), who,
      heading: !!o.heading, ...(o.bold ? { bold: true } : {}), ...(o.lunch ? { lunch: true } : {}),
    }))
  }
  // ISO 50001 & ISO 46001 together: the CCIS energy/water plan, as written (columns follow the standards' order).
  if (isPair50K46K(standards)) {
    return MASTER_50K46K.map(([element, c50, c46, who, o = {}]) => ({
      element, clauses: (standards || []).map(s => (s === PAIR_50K46K[0] ? c50 : c46)), who,
      heading: false, ...(o.bold ? { bold: true } : {}),
    }))
  }
  // ISO 37001 on its own: the CCIS anti-bribery plan, as written.
  if (isOnly37K(standards)) {
    return MASTER_37K.map(([element, clause, who, o = {}]) => ({
      element, clauses: [clause], who, heading: !!o.heading, ...(o.bold ? { bold: true } : {}),
    }))
  }
  const keys = keysOf(standards)
  const has9 = keys.includes('9K')
  const only27 = keys.filter(Boolean).length === 1 && keys.includes('27K')
  const rows = []
  const add = list => {
    for (const [e, c, who, part, o = {}] of list) {
      const clauses = keys.map(k => (k && c[k]) || '')
      if (!clauses.some(Boolean)) continue
      // Lunch: before the first afternoon row (ISO/IEC 27001 alone: before the Annex A controls).
      const pm = only27 ? !!o.annexA : part === 'pm'
      rows.push({ element: has9 && o.e9 ? o.e9 : e, clauses, who, heading: false, pm })
    }
  }
  if (keys.some(k => k && k !== '17K')) add(MASTER)
  if (keys.includes('17K')) add(MASTER_17025)
  const first = rows.findIndex(r => r.pm)
  return rows.map(({ pm, ...r }, i) => (i === first ? { ...r, lunch: true } : r))
}

// Tick / untick the site inspection on a plan's items (always added as the last row). Also moves an existing site
// inspection row (older plans put it before "Internal audit") to the end.
export function withSiteWalk(items, on, standards) {
  const rest = (items || []).filter(it => !it.siteWalk)
  if (!on) return rest
  return [...rest, { ...SITE_INSPECTION, clauses: keysOf(standards).map(() => ''), heading: false }]
}

// ── Audit days ───────────────────────────────────────────────────────────────────────────────────────────────
// plan.days = [{ date, part: 'full' | 'am' | 'pm', start: 'HH:MM', end?: 'HH:MM', who: 'both' | 'a1' | 'a2' }]. Day 1's date is the
// job's audit date. Man-days do not decide the days (2 man-days can be 1 auditor × 2 days or 2 auditors × 1 day),
// so the office lists the days; the plan items are spread over them.
export const PARTS = {
  full: { label: 'Full day',  start: '09:00', minutes: 510, lunch: true },
  am:   { label: 'Morning',   start: '09:00', minutes: 210 },
  pm:   { label: 'Afternoon', start: '14:00', minutes: 210 },
}
export const newDay = (part = 'full') => ({ date: '', part, start: PARTS[part].start, who: 'both' })
// Close-out day (TA2) — owner, 4 Nov 2026: no clauses, one "Close-out of NC / Obs (if any)" row. At most one, always
// the last day; usually a morning. Its date may stay "to be confirmed". Days without the flag are audit days.
export const newCloseoutDay = () => ({ ...newDay('am'), closeout: true })
export const CLOSEOUT_TEXT = 'Close-out of NC / Obs (if any)'

// The plan's days, with Day 1 on the audit date. Plans saved before days existed ({ duration, start }) → one day.
// Day 1 is always an audit day; a close-out day is always kept last (only the first one counts).
export function planDays(plan, auditDate) {
  const raw = Array.isArray(plan?.days) && plan.days.length ? plan.days
    : [{ date: '', part: PARTS[plan?.duration] ? plan.duration : 'full', start: plan?.start || PARTS[plan?.duration || 'full']?.start || '09:00', who: 'both' }]
  const tidy = raw.map((d, i) => (i === 0 && d.closeout ? { ...d, closeout: false } : d))
  const co = tidy.find(d => d.closeout)
  const days = co ? [...tidy.filter(d => !d.closeout), co] : tidy
  return days.map((d, i) => ({ ...newDay(PARTS[d.part] ? d.part : 'full'), ...d, ...(i === 0 && auditDate !== undefined ? { date: auditDate || '' } : {}) }))
}

const toMin = t => { const [h, m] = String(t || '09:00').split(':').map(Number); return (h || 0) * 60 + (m || 0) }
export const fmt12 = mins => {
  const h = Math.floor(mins / 60) % 24, m = mins % 60
  return `${((h + 11) % 12) + 1}.${String(m).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`
}
// A day's start / end in minutes. The end is the default for its length (full day 8.5 h, half day 3.5 h) unless the
// office set a later or earlier end (d.end); an end too close to the start falls back to the default.
function dayMins(d) {
  const p = PARTS[d.part] || PARTS.full
  const start = toMin(d.start || p.start)
  const min = start + (p.lunch ? 300 : 60)          // a full day must leave room for the morning, lunch and some afternoon
  const end = d.end && toMin(d.end) >= min ? toMin(d.end) : start + p.minutes
  return { start, end }
}
const hhmm = m => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
// The end time as 'HH:MM' for the time box (the default when not changed).
export const dayEnd = d => hhmm(dayMins(d).end)
// Start / end of one day, e.g. { from: '9.00am', to: '5.30pm' }.
export function dayTimes(d) {
  const { start, end } = dayMins(d)
  return { from: fmt12(start), to: fmt12(end) }
}

// The timetable, day by day: [{ n, date, who, rows: [meeting | items | break] }].
//   • Day 1 opens with the opening meeting; the last day ends with the closing meeting.
//   • A full day is two sessions (morning, lunch, afternoon); a morning / afternoon is one session.
//   • First session: the top-management items (up to the lunch mark). The functional-heads items are spread evenly
//     over the remaining sessions, in order. One session only (half-day audit): all items.
export function planSchedule(plan, auditDate) {
  const days = planDays(plan, auditDate)
  const items = plan?.items || []
  const sessions = []
  days.forEach((d, di) => {
    if (d.closeout) return                                  // the close-out day carries no plan items
    const p = PARTS[d.part] || PARTS.full
    const { start, end } = dayMins(d)
    if (p.lunch) {
      sessions.push({ di, from: start, to: start + 210 })
      sessions.push({ di, from: start + 270, to: end, afterLunch: true })
    } else sessions.push({ di, from: start, to: end })
  })
  // Lunch mark; plans saved before it: the first functional-heads row, else halfway.
  let split = items.findIndex(it => it.lunch)
  if (split === -1) split = items.findIndex(it => !it.heading && it.who === FH)
  if (split === -1) split = Math.ceil(items.length / 2)
  const blocks = sessions.map(() => [])
  if (sessions.length === 1) blocks[0] = items
  else {
    blocks[0] = items.slice(0, split)
    const rest = items.slice(split), n = sessions.length - 1
    let at = 0
    for (let s = 1; s <= n; s++) { const take = Math.ceil((rest.length - at) / (n - s + 1)); blocks[s] = rest.slice(at, at + take); at += take }
  }
  // The closing meeting ends the last AUDIT day (findings are presented then) and the close-out day.
  const lastAudit = days.map(d => !d.closeout).lastIndexOf(true)
  return days.map((d, di) => {
    const rows = []
    const mine = sessions.map((s, si) => ({ ...s, items: blocks[si] })).filter(s => s.di === di)
    const t = dayTimes(d)
    if (di === 0) rows.push({ time: t.from, text: 'Opening Meeting — Top management, management representatives and functional heads', kind: 'meeting' })
    if (d.closeout) rows.push({ kind: 'items', items: [{ element: CLOSEOUT_TEXT, clauses: [], who: FH, closeout: true }], time: `${t.from} – ${t.to}` })
    mine.forEach(s => {
      if (s.afterLunch) rows.push({ time: `${fmt12(s.from - 60)} – ${fmt12(s.from)}`, text: 'Lunch Break', kind: 'break' })
      if (s.items.length) rows.push({ kind: 'items', items: s.items, time: `${fmt12(s.from)} – ${fmt12(s.to)}` })
    })
    rows.push(di === lastAudit || d.closeout
      ? { time: t.to, text: 'Closing meeting for the day • End of the Day', kind: 'meeting' }
      : { time: t.to, text: `End of Day ${di + 1}`, kind: 'meeting' })
    return { n: di + 1, date: d.date, part: d.part, who: d.who || 'both', closeout: !!d.closeout, rows, ...t }
  })
}

// "ISO 9001:2015 AUDIT PLAN" / "ISO 9001:2015 & ISO 14001:2015 COMBINED AUDIT PLAN" / QEHS
export function planTitle(standards) {
  const s = standards || []
  if (s.length === 3 && ['ISO 9001:2015', 'ISO 14001:2015', 'ISO 45001:2018'].every(x => s.includes(x)))
    return 'ISO 9001:2015, ISO 14001:2015 & ISO 45001:2018 QEHS AUDIT PLAN'
  if (s.length <= 1) return `${s[0] || 'ISO'} AUDIT PLAN`
  return `${s.slice(0, -1).join(', ')} & ${s[s.length - 1]} COMBINED AUDIT PLAN`
}

// A new plan: location ALWAYS empty (as bizSAFE — never the company address). Half-day audit (0.5 man-day) → one
// morning; otherwise one full day. ISO/IEC 17025 → site inspection on.
export function newPlan(standards, { mandays } = {}) {
  // Site inspection is never pre-ticked (owner, 4 Nov 2026) — not even for ISO/IEC 17025; only a person ticks it.
  return { days: [newDay(Number(mandays) === 0.5 ? 'am' : 'full')], location: '', siteWalk: false, version: PLAN_VERSION,
    items: defaultPlanItems(standards) }
}

